import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Loader2, ClipboardCheck, Lock, FileText, Download, CheckCircle2,
  Pencil, FileCheck2, CalendarRange,
} from 'lucide-react';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cents = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const safeParse = (s) => { try { return parseISO(s); } catch { return null; } };

const PAY_BADGE = {
  PAID: 'bg-green-100 text-green-800 border-green-300',
  PARTIAL: 'bg-amber-100 text-amber-800 border-amber-300',
  UNPAID: 'bg-red-100 text-red-700 border-red-300',
};

// Default range = last 7 days
function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

export default function WeeklyReviewPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const dr = defaultRange();
  const [start, setStart] = useState(dr.start);
  const [end, setEnd] = useState(dr.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [title, setTitle] = useState('');
  const [approving, setApproving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [reports, setReports] = useState([]);

  // Edit dialog
  const [editJob, setEditJob] = useState(null);
  const [editDue, setEditDue] = useState('');
  const [editPaid, setEditPaid] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/weekly-reports`, { headers });
      setReports(res.data || []);
    } catch (e) { /* non-blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/dashboard/weekly-review?start=${start}&end=${end}`, { headers });
      setData(res.data);
      // Pre-select all by default
      const sel = {};
      (res.data.jobs || []).forEach((j) => { sel[j.bookingId] = true; });
      setSelected(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load weekly review');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const jobs = data?.jobs || [];
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectedJobs = jobs.filter((j) => selected[j.bookingId]);
  const selTotals = selectedJobs.reduce((acc, j) => {
    acc.due += j.amountDueCents || 0;
    acc.paid += j.amountPaidCents || 0;
    return acc;
  }, { due: 0, paid: 0 });

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const allSelected = jobs.length > 0 && selectedIds.length === jobs.length;
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const sel = {}; jobs.forEach((j) => { sel[j.bookingId] = true; }); setSelected(sel); }
  };

  const openEdit = (job) => {
    setEditJob(job);
    setEditDue(job.amountDueCents != null ? (job.amountDueCents / 100).toFixed(2) : '');
    setEditPaid(job.amountPaidCents != null ? (job.amountPaidCents / 100).toFixed(2) : '');
  };

  const saveEdit = async () => {
    if (!editJob) return;
    setSavingEdit(true);
    try {
      const payload = {
        amountDueCents: Math.round((parseFloat(editDue) || 0) * 100),
        amountPaidCents: Math.round((parseFloat(editPaid) || 0) * 100),
      };
      await axios.patch(`${API_URL}/api/dashboard/weekly-review/${editJob.bookingId}`, payload, { headers });
      toast.success('Job updated');
      setEditJob(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const approve = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one job'); return; }
    setApproving(true);
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/weekly-review/approve`, {
        startDate: start, endDate: end, bookingIds: selectedIds, title: title.trim() || undefined,
      }, { headers });
      toast.success('Report finalized & locked');
      setConfirmOpen(false);
      setTitle('');
      await load();
      await loadReports();
      // Auto-download the freshly created report
      const rid = res.data?.report?.id;
      if (rid) downloadPdf(rid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  const downloadPdf = async (reportId) => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/weekly-reports/${reportId}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeeklyReport-${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download PDF');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-5" data-testid="weekly-review-page">
        <div className="flex items-center gap-1">
          <ClipboardCheck className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Weekly Review</h1>
          <PageHelp
            testId="weekly-review"
            title="Weekly Review & Reports"
            purpose="Review completed jobs for a date range, fix any amounts, then approve them into a finalized, downloadable PDF report."
            actions={[
              { label: 'Pick a range', desc: 'choose the week (or any span) you want to review' },
              { label: 'Edit amounts', desc: 'correct billed/paid amounts before approving' },
              { label: 'Approve & lock', desc: 'finalize selected jobs into a PDF report' },
            ]}
            nextStep={'Select jobs, then tap "Approve & Generate Report".'}
          />
        </div>

        {/* Date range */}
        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label className="flex items-center gap-1"><CalendarRange className="h-4 w-4" /> From</Label>
              <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
                className="mt-1" data-testid="weekly-review-start" />
            </div>
            <div className="flex-1">
              <Label>To</Label>
              <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
                className="mt-1" data-testid="weekly-review-end" />
            </div>
            <Button variant="outline" onClick={load} data-testid="weekly-review-refresh">Refresh</Button>
          </CardContent>
        </Card>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            {/* Review list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">
                  Unapproved Completed Jobs ({jobs.length})
                </h2>
                {jobs.length > 0 && (
                  <button onClick={toggleAll} className="text-sm text-primary" data-testid="weekly-review-select-all">
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>

              {jobs.length === 0 ? (
                <Card data-testid="weekly-review-empty">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    No completed jobs awaiting approval in this range.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2" data-testid="weekly-review-jobs">
                  {jobs.map((job) => (
                    <Card key={job.bookingId} data-testid={`weekly-review-job-${job.bookingId}`}>
                      <CardContent className="p-3 flex items-start gap-3">
                        <Checkbox
                          checked={!!selected[job.bookingId]}
                          onCheckedChange={() => toggle(job.bookingId)}
                          className="mt-1"
                          data-testid={`weekly-review-check-${job.bookingId}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-semibold truncate">{job.clientName}</p>
                            <Badge variant="outline" className={PAY_BADGE[job.paymentStatus]}>{job.paymentStatus}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {job.serviceName}
                            {job.date ? ` · ${format(safeParse(job.date) || new Date(), 'MMM d, yyyy')}` : ''}
                            {job.animalCount ? ` · ${job.animalCount} animal${job.animalCount !== 1 ? 's' : ''}` : ''}
                          </p>
                          {job.location && <p className="text-xs text-muted-foreground truncate">{job.location}</p>}
                          <div className="flex items-center gap-3 mt-1 text-sm tabular-nums">
                            <span>Due <b>{cents(job.amountDueCents)}</b></span>
                            <span className="text-green-700">Paid {cents(job.amountPaidCents)}</span>
                            {job.balanceCents > 0 && <span className="text-red-700">Owes {cents(job.balanceCents)}</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 flex-shrink-0"
                          onClick={() => openEdit(job)} data-testid={`weekly-review-edit-${job.bookingId}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Approve bar */}
            {jobs.length > 0 && (
              <Card className="sticky bottom-2 border-primary/40 shadow-md" data-testid="weekly-review-approve-bar">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-semibold">{selectedIds.length}</span> selected ·
                    Billed <b className="tabular-nums">{cents(selTotals.due)}</b> ·
                    Collected <b className="tabular-nums text-green-700">{cents(selTotals.paid)}</b>
                  </div>
                  <Button disabled={selectedIds.length === 0} onClick={() => setConfirmOpen(true)}
                    data-testid="weekly-review-approve-btn">
                    <Lock className="h-4 w-4 mr-1" /> Approve &amp; Generate Report
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Finalized reports */}
            <div className="pt-2">
              <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
                <FileCheck2 className="h-5 w-5 text-muted-foreground" /> Finalized Reports
              </h2>
              {reports.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="weekly-reports-empty">
                  No finalized reports yet. Approve a week above to create one.
                </p>
              ) : (
                <div className="space-y-2" data-testid="weekly-reports-list">
                  {reports.map((r) => (
                    <Card key={r.id} data-testid={`weekly-report-${r.id}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{r.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {r.summary?.jobCount ?? 0} jobs · Billed {cents(r.summary?.totalDueCents)} ·
                            Collected {cents(r.summary?.totalPaidCents)}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id)}
                          data-testid={`weekly-report-pdf-${r.id}`}>
                          <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Edit job dialog */}
      <Dialog open={!!editJob} onOpenChange={(o) => !o && setEditJob(null)}>
        <DialogContent data-testid="weekly-review-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit job amounts</DialogTitle>
            <DialogDescription>{editJob?.clientName} · {editJob?.serviceName}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount due $</Label>
              <Input type="number" step="0.01" min="0" value={editDue}
                onChange={(e) => setEditDue(e.target.value)} data-testid="weekly-review-edit-due" />
            </div>
            <div>
              <Label>Amount paid $</Label>
              <Input type="number" step="0.01" min="0" value={editPaid}
                onChange={(e) => setEditPaid(e.target.value)} data-testid="weekly-review-edit-paid" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditJob(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="weekly-review-edit-save">
              {savingEdit && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="weekly-review-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Finalize this report?</DialogTitle>
            <DialogDescription>
              {selectedIds.length} job(s) will be locked into a finalized report and can no longer be
              edited from the review screen. A PDF will be generated.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Report title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={`Weekly Report ${start} → ${end}`}
              className="mt-1" data-testid="weekly-review-title-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={approve} disabled={approving} data-testid="weekly-review-confirm-btn">
              {approving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <FileText className="h-4 w-4 mr-1" /> Approve &amp; Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
